import {
  FetchNavigationArticlesQuery,
  FetchNavigationQuery,
} from "@src/generated";

import {
  ALL_GROUPS,
  GroupState,
  NavigationGroup,
  NavigationItem,
  STANDALONE_ITEMS,
} from "../types";

export const PAGE_SIZE = 10;

export function buildPath(
  group: NavigationGroup,
  handle: string,
  blogHandle?: string
): string {
  switch (group) {
    case "pages":
      return `/pages/${handle}`;
    case "collections":
      return `/collections/${handle}`;
    case "products":
      return `/products/${handle}`;
    case "articles":
      return `/blogs/${blogHandle}/${handle}`;
  }
}

type ArticleNode =
  FetchNavigationArticlesQuery["articles"]["edges"][number]["node"];

function edgesToItems(
  group: NavigationGroup,
  edges: FetchNavigationQuery[NavigationGroup]["edges"]
): NavigationItem[] {
  return edges
    .filter((edge) => {
      if (group === "articles") {
        return !!(edge.node as ArticleNode).blog;
      }
      return true;
    })
    .map((edge) => {
      const blogHandle =
        group === "articles"
          ? (edge.node as ArticleNode).blog.handle
          : undefined;
      return {
        title: edge.node.title,
        path: buildPath(group, edge.node.handle, blogHandle),
        group,
      };
    });
}

export const buildNavigationOptions = (
  data: FetchNavigationQuery | undefined,
  extraItems: Partial<Record<NavigationGroup, GroupState>>,
  searchQuery: string | undefined,
  filterGroup?: NavigationGroup
) => {
  const options: NavigationItem[] = [];
  const groupHasMore: Partial<Record<NavigationGroup, boolean>> = {};
  const groupEndCursor: Partial<Record<NavigationGroup, string | null>> = {};

  if (!searchQuery) {
    options.push(...STANDALONE_ITEMS);
  }

  if (!data) {
    return { options, groupHasMore, groupEndCursor };
  }

  const groups = filterGroup ? [filterGroup] : ALL_GROUPS;

  for (const group of groups) {
    const connection = data[group];
    const edges = connection.edges;

    const hasMore = edges.length > PAGE_SIZE;
    const visibleEdges = hasMore ? edges.slice(0, -1) : edges;
    const endCursor =
      visibleEdges.length > 0
        ? visibleEdges[visibleEdges.length - 1].cursor
        : null;

    options.push(...edgesToItems(group, visibleEdges));

    const extra = extraItems[group];
    if (extra) {
      options.push(...extra.items);
      groupHasMore[group] = extra.hasMore;
      groupEndCursor[group] = extra.endCursor;
    } else {
      groupHasMore[group] = hasMore;
      groupEndCursor[group] = endCursor;
    }
  }

  return { options, groupHasMore, groupEndCursor };
};
